// ── Section Types ──

export enum SectionType {
  FIX = 'FIX',
  CAS_IN = 'CAS-IN',
  CAS_OUT = 'CAS-OUT',
  TT = 'TT',
  TILT_IN = 'TILT-IN',
  TOPHUNG_OUT = 'TOPHUNG-OUT',
  STOLP = 'STOLP',
  PIVOT_V = 'PIVOT-V',
  SLIDE_H = 'SLIDE-H',
  SLIDE_V = 'SLIDE-V',
}

export enum SectionState {
  CLOSED = 'closed',
  OPEN = 'open',
  TILT = 'tilt',
  TURN = 'turn',
}

export enum MaterialFamily {
  WOOD = 'wood',
  UPVC = 'upvc',
  ALUMINIUM = 'aluminium',
  STEEL = 'steel',
  WOOD_ALU = 'wood-aluminium',
}

export enum GlazingTier {
  HR = 'HR',
  HR_PLUS = 'HR+',
  HR_PLUS_PLUS = 'HR++',
  HR_TRIPLE = 'HR+++',
  VACUUM = 'vacuum',
}

export type RoedenMode = 'none' | 'true-divided' | 'applied' | 'between-glass';

export type RoedenPattern =
  | 'none'
  | '1x2' | '2x1' | '2x2'
  | '3x1' | '1x3' | '3x2' | '2x3'
  | 'perimeter'
  | 'custom';

export type HandleFinish = 'chrome' | 'black' | 'white' | 'brushed-steel';

// ── Layout AST ──

export type LayoutNode =
  | { kind: 'leaf'; index: number }
  | { kind: 'hsplit'; children: LayoutNode[] }
  | { kind: 'vsplit'; children: LayoutNode[] };

export interface SectionRect {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Per-edge insets telling a section how much space is occupied by adjacent mullions. */
export interface SectionInsets {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

// ── Configuration ──

export interface RoedenConfig {
  mode: RoedenMode;
  pattern: RoedenPattern;
  customCols?: number;
  customRows?: number;
}

export interface SectionConfig {
  type: SectionType;
  orientation: {
    hingeSide?: 'left' | 'right';
    swingDir?: 'inward' | 'outward';
    pivotOffset?: number;
    trackCount?: number;
  };
  hardware: {
    handleType: 'standard' | 'lockable';
    handleFinish: HandleFinish;
    handlePosition: 'left' | 'right' | 'bottom' | 'top';
    restrictor: boolean;
  };
  infill: {
    type: 'glazing' | 'opaque';
    glazingTier?: GlazingTier;
    tint?: string;
    safety?: 'laminated' | 'toughened' | 'none';
    acoustic?: boolean;
    solarControl?: boolean;
  };
  roeden: RoedenConfig;
  openState: SectionState;
  openAmount: number;
}

export interface WindowConfig {
  width: number;
  height: number;

  layout: {
    presetId: string;
    expression: string;
    mirrorLR: boolean;
    flipTB: boolean;
  };

  frame: {
    material: MaterialFamily;
    insideColor: string;
    outsideColor: string;
  };

  sections: SectionConfig[];

  glazingTier: GlazingTier;

  ventilation: {
    enabled: boolean;
    type: 'frame' | 'glass';
    position: 'top' | 'bottom';
  };

  insectScreen: {
    enabled: boolean;
  };

  shading: {
    enabled: boolean;
  };
}

// ── Section interface for animation ──

export interface SectionInterface {
  setOpenAngle?: (rad: number) => void;
  setTiltAngle?: (rad: number) => void;
  setTranslation?: (t: number) => void;
  group: import('three').Group;
}
